import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  FolderTree,
  ChevronDown,
  ChevronRight,
  Server as ServerIcon,
  AlertTriangle,
  CheckCircle,
  Clock,
  Scan,
  Rocket,
  Power,
} from "lucide-react";
import {
  servicesApi,
  scansApi,
  groupsApi,
  Service,
  ServiceServer,
  ServerComponent,
  Scan as ScanType,
  ScanResult,
  SDLCPathResponse,
} from "@/api/client";
import DeploymentDetailsModal from "@/components/DeploymentDetailsModal";
import { formatDate, titleCase } from "@/lib/utils";
import GroupSelector, { GroupSelectField } from "@/components/GroupSelector";

export default function Services() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(
    searchParams.get("group"),
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);

  // Modal state
  const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);

  // Sync groupFilter with URL
  useEffect(() => {
    const urlGroup = searchParams.get("group");
    if (urlGroup !== groupFilter) {
      setGroupFilter(urlGroup);
    }
  }, [searchParams]);

  // Update URL when filter changes
  const handleGroupFilterChange = (value: string | null) => {
    setGroupFilter(value);
    if (value) {
      setSearchParams({ group: value });
    } else {
      setSearchParams({});
    }
  };

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["services", groupFilter],
    queryFn: () =>
      servicesApi.list(groupFilter ? { group: groupFilter } : undefined),
  });

  const { data: serviceServersData } = useQuery({
    queryKey: ["service-servers", expandedService],
    queryFn: () =>
      expandedService ? servicesApi.getServers(expandedService) : null,
    enabled: !!expandedService,
  });

  // Query for scan details when modal is open
  const { data: scanDetails, isLoading: scanLoading } = useQuery({
    queryKey: ["scan", selectedScanId],
    queryFn: () => (selectedScanId ? scansApi.get(selectedScanId) : null),
    enabled: !!selectedScanId,
  });

  const createMutation = useMutation({
    mutationKey: ["createService"],
    mutationFn: servicesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setShowForm(false);
      setSelectedGroupId(null);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateService"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof servicesApi.update>[1]) =>
      servicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setEditingService(null);
      setSelectedGroupId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteService"],
    mutationFn: servicesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const serviceData = {
      name: formData.get("name") as string,
      slug: (formData.get("slug") as string) || undefined,
      description: formData.get("description") as string,
      repository_url: formData.get("repository_url") as string,
      group_id: selectedGroupId || undefined,
    };

    if (editingService) {
      updateMutation.mutate({ id: editingService.id, ...serviceData });
    } else {
      createMutation.mutate(serviceData);
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setSelectedGroupId(service.group_id || null);
  };

  const handleDelete = (id: string, name: string) => {
    if (
      confirm(
        `Are you sure you want to delete "${name}"? This will also delete all deployment history for this service.`,
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingService(null);
    setSelectedGroupId(null);
  };

  const toggleExpand = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const servers = serviceServersData?.servers || [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Services</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage the services you deploy
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingService(null);
            setSelectedGroupId(null);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </button>
      </div>

      {/* Group Filter */}
      <div className="mt-4">
        <GroupSelector
          value={groupFilter}
          onChange={handleGroupFilterChange}
          includeAll={true}
          className="max-w-xs"
        />
      </div>

      {/* Form */}
      {(showForm || editingService) && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingService ? "Edit Service" : "Add Service"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="label">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="input"
                  placeholder="API Gateway"
                  defaultValue={editingService?.name}
                  required
                />
              </div>
              <div>
                <label htmlFor="slug" className="label">
                  Slug (optional)
                </label>
                <input
                  type="text"
                  name="slug"
                  id="slug"
                  className="input"
                  placeholder="api-gateway"
                  defaultValue={editingService?.slug}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Auto-generated from name if empty
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="repository_url" className="label">
                Repository URL (optional)
              </label>
              <input
                type="url"
                name="repository_url"
                id="repository_url"
                className="input"
                placeholder="https://github.com/org/repo"
                defaultValue={editingService?.repository_url}
              />
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description (optional)
              </label>
              <textarea
                name="description"
                id="description"
                rows={2}
                className="input"
                placeholder="What does this service do?"
                defaultValue={editingService?.description}
              />
            </div>

            <GroupSelectField
              value={selectedGroupId}
              onChange={setSelectedGroupId}
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : "Save Service"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Services List */}
      <div className="mt-6 grid gap-4">
        {isLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (data?.services ?? []).length === 0 ? (
          <div className="card text-center py-8">
            <p className="text-gray-500">
              {groupFilter
                ? "No services in this group. Add one or change the filter."
                : "No services yet. Add your first service to get started."}
            </p>
          </div>
        ) : (
          <ServicesByTier
            services={data?.services ?? []}
            expandedService={expandedService}
            toggleExpand={toggleExpand}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            servers={servers}
            setSelectedScanId={setSelectedScanId}
            setSelectedDeploymentId={setSelectedDeploymentId}
          />
        )}
      </div>

      {/* Scan Details Modal */}
      {selectedScanId && (
        <ScanDetailsModal
          scan={scanDetails?.scan}
          results={scanDetails?.results || []}
          isLoading={scanLoading}
          onClose={() => setSelectedScanId(null)}
        />
      )}

      {/* Deployment Details Modal */}
      {selectedDeploymentId && (
        <DeploymentDetailsModal
          deploymentId={selectedDeploymentId}
          onClose={() => setSelectedDeploymentId(null)}
        />
      )}
    </div>
  );
}

// Tier labels and ordering
const tierOrder = [
  "frontend",
  "backend",
  "persistence",
  "data",
  "infrastructure",
  "monitoring",
  "other",
] as const;
const tierLabels: Record<string, string> = {
  frontend: "Primary Applications",
  backend: "Backend Services",
  persistence: "Persistence Layer",
  data: "Databases",
  infrastructure: "Infrastructure",
  monitoring: "Monitoring",
  other: "Other Services",
};

// Component to group and display services by tier
function ServicesByTier({
  services,
  expandedService,
  toggleExpand,
  handleEdit,
  handleDelete,
  servers,
  setSelectedScanId,
  setSelectedDeploymentId,
}: {
  services: Service[];
  expandedService: string | null;
  toggleExpand: (id: string) => void;
  handleEdit: (service: Service) => void;
  handleDelete: (id: string, name: string) => void;
  servers: ServiceServer[];
  setSelectedScanId: (id: string | null) => void;
  setSelectedDeploymentId: (id: string | null) => void;
}) {
  // Group services by tier
  const servicesByTier = services.reduce(
    (acc, service) => {
      const tier = service.tier || "other";
      if (!acc[tier]) acc[tier] = [];
      acc[tier].push(service);
      return acc;
    },
    {} as Record<string, Service[]>,
  );

  // Get tiers that have services, in the correct order
  const activeTiers = tierOrder.filter(
    (tier) => servicesByTier[tier]?.length > 0,
  );

  return (
    <>
      {activeTiers.map((tier) => (
        <div key={tier}>
          {/* Tier Header - only show if there are multiple tiers */}
          {activeTiers.length > 1 && (
            <div className="flex items-center gap-2 mb-3 mt-2 first:mt-0">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                {tierLabels[tier] || tier}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
          )}

          {/* Services in this tier */}
          <div className="grid gap-4 mb-4">
            {servicesByTier[tier].map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                isExpanded={expandedService === service.id}
                onToggleExpand={() => toggleExpand(service.id)}
                onEdit={() => handleEdit(service)}
                onDelete={() => handleDelete(service.id, service.name)}
                servers={expandedService === service.id ? servers : []}
                onSelectScan={setSelectedScanId}
                onSelectDeployment={setSelectedDeploymentId}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

// Individual service card component
function ServiceCard({
  service,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  servers,
  onSelectScan,
  onSelectDeployment,
}: {
  service: Service;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  servers: ServiceServer[];
  onSelectScan: (id: string | null) => void;
  onSelectDeployment: (id: string | null) => void;
}) {
  // Fetch SDLC path for sorting environments when expanded
  const { data: sdlcPath } = useQuery({
    queryKey: ["sdlc-path", service.group_id],
    queryFn: () =>
      service.group_id ? groupsApi.getSDLCPath(service.group_id) : null,
    enabled: !!service.group_id && isExpanded,
  });

  return (
    <div className="card">
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-start gap-2">
          <div className="mt-1">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900">
                {titleCase(service.name)}
              </h3>
              {service.is_primary && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  Primary
                </span>
              )}
              {service.group_name && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-800">
                  <FolderTree className="h-3 w-3" />
                  {service.group_name}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{service.slug}</p>
            {service.description && (
              <p className="mt-2 text-sm text-gray-600">
                {service.description}
              </p>
            )}
            {service.repository_url && (
              <a
                href={service.repository_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center text-sm text-brand-600 hover:text-brand-700"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Repository
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-2 text-gray-400 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Expanded Servers Section - Grouped by Environment */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {servers.length === 0 ? (
            <p className="text-sm text-gray-500">
              No servers assigned to this service
            </p>
          ) : (
            <EnvironmentServerGroups
              servers={servers}
              sdlcPath={sdlcPath}
              onSelectScan={onSelectScan}
              onSelectDeployment={onSelectDeployment}
            />
          )}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
        Created {formatDate(service.created_at)}
      </div>
    </div>
  );
}

// Scan Details Modal Component
function ScanDetailsModal({
  scan,
  results,
  isLoading,
  onClose,
}: {
  scan?: ScanType;
  results: ScanResult[];
  isLoading: boolean;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[80vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">
            Loading scan details...
          </div>
        ) : !scan ? (
          <div className="p-6 text-center text-gray-500">Scan not found</div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Scan className="h-5 w-5 text-brand-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {scan.scan_type === "initial"
                        ? "Initial Scan"
                        : scan.scan_type === "daily"
                          ? "Daily Scan"
                          : "Scan"}{" "}
                      Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      {scan.environment_name} • {formatDate(scan.started_at)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {scan.servers_scanned}
                  </div>
                  <div className="text-xs text-gray-500">Servers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {scan.components_checked}
                  </div>
                  <div className="text-xs text-gray-500">Components</div>
                </div>
                <div>
                  <div
                    className={`text-2xl font-bold ${scan.drift_count > 0 ? "text-orange-600" : "text-green-600"}`}
                  >
                    {scan.drift_count}
                  </div>
                  <div className="text-xs text-gray-500">Drift</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 capitalize">
                    {scan.status}
                  </div>
                  <div className="text-xs text-gray-500">Status</div>
                </div>
              </div>
              {scan.source_url && (
                <div className="mt-3 text-center">
                  <a
                    href={scan.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View in {scan.source || "Source"} →
                  </a>
                </div>
              )}
            </div>

            {/* Results Table */}
            <div className="px-6 py-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Scan Results
              </h3>
              {results.length === 0 ? (
                <p className="text-sm text-gray-500">No results recorded</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Component
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Service
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Version
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {results.map((result) => (
                        <tr
                          key={result.id}
                          className={result.is_drift ? "bg-orange-50" : ""}
                        >
                          <td className="px-3 py-2">
                            <div className="text-gray-900">
                              {result.component_name ||
                                result.component_identifier}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-500">
                            {result.service_name || "-"}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-900">
                            {result.detected_version}
                          </td>
                          <td className="px-3 py-2">
                            {result.is_drift ? (
                              <span className="inline-flex items-center gap-1 text-orange-600">
                                <AlertTriangle className="h-3 w-3" />
                                Drift
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <Power className="h-3 w-3" />
                                OK
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Group servers by environment
function EnvironmentServerGroups({
  servers,
  sdlcPath,
  onSelectScan,
  onSelectDeployment,
}: {
  servers: ServiceServer[];
  sdlcPath?: SDLCPathResponse | null;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const [expandedEnvs, setExpandedEnvs] = useState<Set<string>>(new Set());

  // Group servers by environment
  const serversByEnv = servers.reduce(
    (acc, server) => {
      const envId = server.environment_id || "unknown";
      const envName = server.environment_name || "Unknown Environment";
      if (!acc[envId]) {
        acc[envId] = { name: envName, servers: [] };
      }
      acc[envId].servers.push(server);
      return acc;
    },
    {} as Record<string, { name: string; servers: ServiceServer[] }>,
  );

  // Sort environments by SDLC path (prod first, then down the path, off-path at end)
  const sortedEnvIds = useMemo(() => {
    const ids = Object.keys(serversByEnv);

    if (!sdlcPath?.in_path || sdlcPath.in_path.length === 0) {
      return ids; // No SDLC path configured, keep original order
    }

    // Create map of environment ID to SDLC order (reversed: prod first)
    const sdlcOrderMap = new Map<string, number>();
    const reversedPath = [...sdlcPath.in_path].reverse();
    reversedPath.forEach((env, index) => {
      sdlcOrderMap.set(env.id, index);
    });

    return ids.sort((a, b) => {
      const aOrder = sdlcOrderMap.get(a);
      const bOrder = sdlcOrderMap.get(b);

      // Both in path - sort by SDLC order
      if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
      // Only a is in path - a comes first
      if (aOrder !== undefined) return -1;
      // Only b is in path - b comes first
      if (bOrder !== undefined) return 1;
      // Neither in path - sort alphabetically
      return serversByEnv[a].name.localeCompare(serversByEnv[b].name);
    });
  }, [serversByEnv, sdlcPath]);

  const toggleEnv = (envId: string) => {
    setExpandedEnvs((prev) => {
      const next = new Set(prev);
      if (next.has(envId)) {
        next.delete(envId);
      } else {
        next.add(envId);
      }
      return next;
    });
  };

  // Calculate drift stats per environment
  const getEnvStats = (envServers: ServiceServer[]) => {
    let totalDrift = 0;
    let totalPending = 0;
    let totalComponents = 0;
    envServers.forEach((server) => {
      server.components?.forEach((comp) => {
        totalComponents++;
        const status = computeComponentStatus(comp);
        if (status.isDrift) totalDrift++;
        if (status.isPendingScan) totalPending++;
      });
    });
    return { totalDrift, totalPending, totalComponents };
  };

  return (
    <div className="space-y-4">
      {sortedEnvIds.map((envId) => {
        const { name, servers: envServers } = serversByEnv[envId];
        const isExpanded = expandedEnvs.has(envId);
        const stats = getEnvStats(envServers);

        return (
          <div key={envId} className="border border-gray-200 rounded-lg">
            <div
              className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 rounded-t-lg"
              onClick={() => toggleEnv(envId)}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
                <span className="font-medium text-gray-900">{name}</span>
                <span className="text-xs text-gray-500">
                  ({envServers.length} server
                  {envServers.length !== 1 ? "s" : ""})
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  {stats.totalComponents} components
                </span>
                {stats.totalDrift > 0 && (
                  <span className="flex items-center gap-1 text-xs text-red-600">
                    <AlertTriangle className="h-3 w-3" />
                    {stats.totalDrift} drift
                  </span>
                )}
                {stats.totalPending > 0 && (
                  <span className="flex items-center gap-1 text-xs text-amber-600">
                    <Clock className="h-3 w-3" />
                    {stats.totalPending} pending
                  </span>
                )}
                {stats.totalDrift === 0 && stats.totalPending === 0 && (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    OK
                  </span>
                )}
              </div>
            </div>
            {isExpanded && (
              <div className="p-3 space-y-3">
                {envServers.map((server) => (
                  <ServerCard
                    key={server.id}
                    server={server}
                    onSelectScan={onSelectScan}
                    onSelectDeployment={onSelectDeployment}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Helper to compute valid drift status based on timestamps
function computeComponentStatus(comp: ServerComponent): {
  isDrift: boolean;
  isPendingScan: boolean;
} {
  // If versions match, no drift
  if (
    !comp.expected_version ||
    !comp.actual_version ||
    comp.expected_version === comp.actual_version
  ) {
    return { isDrift: false, isPendingScan: false };
  }

  // Versions differ - check if we've scanned after setting the baseline
  const lastScanned = comp.last_scanned_at
    ? new Date(comp.last_scanned_at)
    : null;
  const baselineSet = comp.baseline_set_at
    ? new Date(comp.baseline_set_at)
    : null;

  // If no baseline_set_at, fall back to the database is_drift flag
  if (!baselineSet) {
    return { isDrift: comp.is_drift, isPendingScan: false };
  }

  // If we haven't scanned since the baseline was set, it's pending scan (not drift)
  if (!lastScanned || lastScanned <= baselineSet) {
    return { isDrift: false, isPendingScan: true };
  }

  // We've scanned after baseline was set, so this is real drift
  return { isDrift: true, isPendingScan: false };
}

// Server card component with expandable components
function ServerCard({
  server,
  onSelectScan,
  onSelectDeployment,
}: {
  server: ServiceServer;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Count drift using timestamp-aware logic
  const driftCount =
    server.components?.filter((c) => computeComponentStatus(c).isDrift)
      .length || 0;
  const pendingScanCount =
    server.components?.filter((c) => computeComponentStatus(c).isPendingScan)
      .length || 0;

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <ServerIcon className="h-4 w-4 text-gray-400" />
          <div>
            <span className="font-medium text-sm text-gray-900">
              {server.hostname}
            </span>
            {server.ip_address && (
              <span className="ml-2 text-xs text-gray-500">
                {server.ip_address}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {server.environment_name}
          </span>
          <span className="text-xs text-gray-500">
            {server.components?.length || 0} components
          </span>
          {driftCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle className="h-3 w-3" />
              {driftCount} drift
            </span>
          )}
          {pendingScanCount > 0 && (
            <span
              className="flex items-center gap-1 text-xs text-amber-600"
              title="Versions differ but no scan has run since the last deployment"
            >
              <Clock className="h-3 w-3" />
              {pendingScanCount} pending scan
            </span>
          )}
          {driftCount === 0 && pendingScanCount === 0 && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="h-3 w-3" />
              OK
            </span>
          )}
        </div>
      </div>

      {/* Expanded Components */}
      {expanded && server.components && server.components.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left py-1 font-medium">Component</th>
                <th className="text-left py-1 font-medium">Version</th>
                <th className="text-left py-1 font-medium">Version Set</th>
                <th className="text-left py-1 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {server.components.map((comp: ServerComponent) => (
                <ComponentRow
                  key={comp.id}
                  comp={comp}
                  onSelectScan={onSelectScan}
                  onSelectDeployment={onSelectDeployment}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Component row with version set click handler
function ComponentRow({
  comp,
  onSelectScan,
  onSelectDeployment,
}: {
  comp: ServerComponent;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const status = computeComponentStatus(comp);
  const date = comp.baseline_set_at ? formatDate(comp.baseline_set_at) : "";

  const handleVersionSetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (comp.baseline_set_by === "deployment" && comp.baseline_deployment_id) {
      onSelectDeployment(comp.baseline_deployment_id);
    } else if (
      comp.baseline_set_by === "initial_scan" &&
      comp.baseline_scan_id
    ) {
      onSelectScan(comp.baseline_scan_id);
    }
  };

  const hasLink =
    (comp.baseline_set_by === "deployment" && comp.baseline_deployment_id) ||
    (comp.baseline_set_by === "initial_scan" && comp.baseline_scan_id);

  return (
    <tr className="border-t border-gray-100">
      <td className="py-1.5 text-gray-900">
        {comp.component_name || comp.component_identifier}
      </td>
      <td className="py-1.5 font-mono text-xs text-gray-600">
        {comp.expected_version || "-"}
      </td>
      <td className="py-1.5 text-xs">
        {hasLink ? (
          <button
            onClick={handleVersionSetClick}
            className="text-left bg-transparent border-none p-0 cursor-pointer"
          >
            <div className="space-y-0.5">
              <span className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline">
                {comp.baseline_set_by === "deployment" ? (
                  <>
                    <Rocket className="h-3 w-3" />
                    Deployment
                  </>
                ) : (
                  <>
                    <Scan className="h-3 w-3" />
                    Initial Scan
                  </>
                )}
              </span>
              {date && <div className="text-xs text-gray-500">{date}</div>}
            </div>
          </button>
        ) : (
          <div className="space-y-0.5">
            <span className="text-gray-500">
              {comp.baseline_set_by === "deployment"
                ? "Deployment"
                : comp.baseline_set_by === "initial_scan"
                  ? "Initial Scan"
                  : "-"}
            </span>
            {date && <div className="text-xs text-gray-500">{date}</div>}
          </div>
        )}
      </td>
      <td className="py-1.5">
        {status.isDrift ? (
          <span className="flex items-center gap-1 text-red-600">
            <AlertTriangle className="h-3 w-3" />
            Drift
          </span>
        ) : status.isPendingScan ? (
          <span
            className="flex items-center gap-1 text-amber-600"
            title="No scan since last deployment"
          >
            <Clock className="h-3 w-3" />
            Pending Scan
          </span>
        ) : (
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-3 w-3" />
            OK
          </span>
        )}
      </td>
    </tr>
  );
}
